import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useToast } from '@codes/ui-kit/components/ui/use-toast';

import type { KeyView } from '@codes/codes-keys';
import { useCodesKeyList, useDeleteCodesKey, useRegenerateCodesKey } from '@codes/codes-keys';

import type { CallbackInfo, NewlyCreatedKey } from '../types';

export const useCodesKeys = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [searchParams] = useSearchParams();

    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedKey, setSelectedKey] = useState<KeyView | null>(null);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState<NewlyCreatedKey | null>(null);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState<KeyView | null>(null);

    const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
    const [keyToRegenerate, setKeyToRegenerate] = useState<KeyView | null>(null);

    const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
    const [keyToValidate, setKeyToValidate] = useState<KeyView | null>(null);

    const callbackInfo: CallbackInfo | null = useMemo(() => {
        const callback = searchParams.get('callback');
        const state = searchParams.get('state');
        const origin = searchParams.get('origin');

        if (callback === 'flow' && state && origin) {
            return { state, origin };
        }
        return null;
    }, [searchParams]);

    const keysQuery = useCodesKeyList({ params: { view: 'user' } });
    const deleteMutation = useDeleteCodesKey();
    const regenerateMutation = useRegenerateCodesKey();

    const handleCreate = (): void => {
        setDialogMode('create');
        setSelectedKey(null);
        setIsFormDialogOpen(true);
    };

    const handleEdit = (key: KeyView): void => {
        setDialogMode('edit');
        setSelectedKey(key);
        setIsFormDialogOpen(true);
    };

    const handleDelete = (key: KeyView): void => {
        setKeyToDelete(key);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!keyToDelete?.id) {
            return;
        }

        try {
            await deleteMutation.mutateAsync({ id: keyToDelete.id });
            toast({
                title: t('codesKeys.deleteSuccess'),
            });

            setIsDeleteDialogOpen(false);
            setKeyToDelete(null);
        } catch (error) {
            toast({
                title: t('codesKeys.error'),
                variant: 'destructive',
            });
        }
    };

    const handleFormSuccess = (key?: NewlyCreatedKey): void => {
        if (key) {
            setNewlyCreatedKey(key);
            setIsSuccessDialogOpen(true);
        }
    };

    const handleRegenerate = (key: KeyView): void => {
        setKeyToRegenerate(key);
        setIsRegenerateDialogOpen(true);
    };

    const confirmRegenerate = async (): Promise<void> => {
        if (!keyToRegenerate?.id) {
            return;
        }

        try {
            const result = await regenerateMutation.mutateAsync({ id: keyToRegenerate.id });
            toast({
                title: t('codesKeys.regenerateSuccess'),
            });

            setIsRegenerateDialogOpen(false);
            setKeyToRegenerate(null);

            setNewlyCreatedKey({
                apiKey: result.apiKey,
                name: result.name || keyToRegenerate.name || 'codes-key',
            });
            setIsSuccessDialogOpen(true);
        } catch (error) {
            toast({
                title: t('codesKeys.error'),
                description: error instanceof Error ? error.message : t('codesKeys.form.unknownError'),
                variant: 'destructive',
            });
        }
    };

    const handleValidate = (key: KeyView): void => {
        setKeyToValidate(key);
        setIsValidationDialogOpen(true);
    };

    return {
        // Query state
        keysData: keysQuery.data,
        isKeysLoading: keysQuery.isLoading,
        isKeysError: keysQuery.isError,
        keysError: keysQuery.error,
        callbackInfo,

        // Form dialog
        formDialog: {
            isOpen: isFormDialogOpen,
            setIsOpen: setIsFormDialogOpen,
            mode: dialogMode,
            selectedKey,
            onSuccess: handleFormSuccess,
        },

        // Success dialog
        successDialog: {
            isOpen: isSuccessDialogOpen,
            setIsOpen: setIsSuccessDialogOpen,
            newlyCreatedKey,
        },

        // Delete dialog
        deleteDialog: {
            isOpen: isDeleteDialogOpen,
            setIsOpen: setIsDeleteDialogOpen,
            keyToDelete,
            isDeleting: deleteMutation.isPending,
            onConfirm: confirmDelete,
        },

        // Regenerate dialog
        regenerateDialog: {
            isOpen: isRegenerateDialogOpen,
            setIsOpen: setIsRegenerateDialogOpen,
            keyToRegenerate,
            isRegenerating: regenerateMutation.isPending,
            onConfirm: confirmRegenerate,
        },

        // Validation dialog
        validationDialog: {
            isOpen: isValidationDialogOpen,
            setIsOpen: setIsValidationDialogOpen,
            keyToValidate,
        },

        // Actions
        handleCreate,
        handleEdit,
        handleDelete,
        handleRegenerate,
        handleValidate,
    };
};
